import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import socket, { register, unregister } from './utils/socket';

import { getToken, isTokenExpired, logout } from './utils/auth';
import emitter from './utils/emitter';

import Map from './components/Map';
import Filter from './components/Filter';
import Login from './components/Login';
import Register from './components/Register';
import LandingRedirect from './components/LandingRedirect';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import OpsMap from './components/ops/OpsMap';
import ParkingHistory from './components/ParkingHistory';
import { AuthProvider, useAuth } from './context/AuthContext';
import backgroundImage from './assets/images/parking_background.png';
import logo from './assets/images/logo.png';

import ProfileModal from './components/ProfileModal';
import ActivityToast from './components/ActivityToast';
import AppPromoCard, { appPromoAlreadySeen } from './components/AppPromoCard';
import { activityKey, activitySeenKey, mergeActivity, pruneActivity } from './utils/activityLog';
import AcceptedRequestModal from './components/AcceptedRequestModal';
import ArrivalRejectedModal from './components/ArrivalRejectedModal';
import ArrivalConfirmationModal from './components/ArrivalConfirmationModal';
import MessagesSideDrawer from './components/MessagesSideDrawer';
import RequestsPanel from './components/RequestsPanel';

import newRequestSound from './assets/sounds/new-request.wav';
import removeRequestSound from './assets/sounds/remove-request.wav';
import acceptedRequestSound from './assets/sounds/accepted-request.wav';
import arrivedSound from './assets/sounds/arrived.wav';
import RatingModal from './components/RatingModal';
import RequesterDetailsModal from './components/RequesterDetailsModal';
import './App.css';

// Matches utils/api.js and utils/socket.js's REACT_APP_SERVER_URL pattern — the several fetch()
// calls below used to hardcode localhost directly instead of taking the serverUrl prop they were
// already passed, silently breaking against any deployed server.
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';

function MainAppContent({ serverUrl }) {
  const [isMessagesDrawerOpen, setIsMessagesDrawerOpen] = useState(false);
  const [showRequesterDetailsModal, setShowRequesterDetailsModal] = useState(false);
  const [selectedRequester, setSelectedRequester] = useState(null);
  const [chatRecipient, setChatRecipient] = useState(null);
  const [allChatMessages, setAllChatMessages] = useState({});
  const [unreadMessages, setUnreadMessages] = useState({});
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const removeRequestAudioBufferRef = useRef(null);
  const acceptedRequestAudioBufferRef = useRef(null);
  const arrivedAudioBufferRef = useRef(null);
  // 2026-10-17: "show spots free in N minutes" is gone. The phone has never had it, and it filters
  // on time_to_leave, which is NULL on an auto-detected park until COMMIT computes an ETA — so it
  // hid real spots, including the ones about to free, which is the opposite of its purpose.
  //
  // fitsMyCarOnly replaces it, matching the phone: a visible toggle, DEFAULT OFF. The web used to
  // apply the same rule invisibly and always, by sending userCarType on every fetch with no way to
  // turn it off and nothing on screen to say it was on. That is what hid this account's own car —
  // the native declare writes declaredCarType from UserDefaults, which falls back to "sedan" on a
  // fresh install, and a sedan does not fit a family car's filter.
  const [fitsMyCarOnly, setFitsMyCarOnly] = useState(false);
  // Kilometres, 0.2-1 since 2026-10-28 (was 1-10), matching the phone's slider; the server clamps to
  // the same range (server/utils/spotRadius.js). The dropdown this replaced offered 2/5/10/20/30,
  // five values picked by eye, and never sent any of them anywhere.
  const [selectedRadius, setSelectedRadius] = useState(1);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAcceptedRequestModal, setShowAcceptedRequestModal] = useState(false);
  // 2026-09-16: owner name for the "couldn't see you" dialog; null while it is closed.
  const [arrivalRejectedBy, setArrivalRejectedBy] = useState(null);
  const [isArrivalConfirmationModalOpen, setArrivalConfirmationModalOpen] = useState(false);
  const [arrivalConfirmationData, setArrivalConfirmationData] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [userToRate, setUserToRate] = useState(null);
  const [acceptedRequestOwnerUsername, setAcceptedRequestOwnerUsername] = useState('');
  const [profileUserData, setProfileUserData] = useState(null);
  const [filteredParkingSpots, setFilteredParkingSpots] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [acceptedSpot, setAcceptedSpot] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  const [currentUserCarType, setCurrentUserCarType] = useState(null);
  // 2026-10-28: the activity log, kept per account for three days and read in Profile → Activity (see
  // utils/activityLog.js). It was a panel along the bottom of the map, held in sessionStorage.
  const [notificationLog, setNotificationLog] = useState([]);
  const [activityLoadedFor, setActivityLoadedFor] = useState(null);
  const [activitySeenAt, setActivitySeenAt] = useState(null);
  const [showAppPromo, setShowAppPromo] = useState(() => !appPromoAlreadySeen());
  const [pendingRequests, setPendingRequests] = useState([]);
  const requesterEta = null;
  const navigate = useNavigate();
  const { role } = useAuth();
  const [spotRequests, setSpotRequests] = useState([]);
  const [isRequestsPanelOpen, setIsRequestsPanelOpen] = useState(false);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [expiredSpotIds, setExpiredSpotIds] = useState([]); // New state for expired spot IDs
  const [spotToOpenDrawer, setSpotToOpenDrawer] = useState(null); // New state to trigger opening a specific spot's drawer
  const [arrivalRejectedSpotId, setArrivalRejectedSpotId] = useState(null); // New state for arrival rejection

  const addNotification = useCallback((message, color = 'default') => {
    if (typeof message !== 'string' || !message) return;
    const now = Date.now();
    setNotificationLog((prevLog) => pruneActivity([...prevLog, { msg: message, at: now, rawTime: now, color }], now));
  }, []);

  const handleLogout = useCallback(() => {
    if (currentUserId) {
      unregister(currentUserId);
    }
    logout();
    // A full document load, not navigate('/'). "/" is the static landing page, and Caddy only
    // rewrites it on a real request — a pushState would resolve "/" inside the router instead.
    window.location.assign('/');
    // The log goes with the session: a shared computer must not show the next person whose spot
    // the last one handed over.
    try {
      if (currentUserId) {
        localStorage.removeItem(activityKey(currentUserId));
        localStorage.removeItem(activitySeenKey(currentUserId));
      }
    } catch (_) { /* storage unavailable: nothing was kept */ }
    setNotificationLog([]);
  }, [currentUserId, navigate, setNotificationLog]);

  // 2026-10-16: this was `setSelectedRequester(requesterData)` with whatever the caller had, and
  // every caller had a REQUEST ROW — requester_id, requester_username, requester_avatar_url.
  // RequesterDetailsModal reads a USER: .username, .car_type, .created_at, .rating. So the card
  // rendered an empty name, "Joined on: N/A", blank car type and "Rank: top undefined %".
  //
  // Worse, its message button did onOpenChat(requester) and the chat opener reads recipient.id —
  // which on a request row is the REQUEST's id, not the person's. It would have opened a
  // conversation against whichever user happened to share that number.
  //
  // So fetch the person, as the phone's RequestsScreen has always done. The shell goes up first so
  // the card appears at once; the fetch fills it in.
  const handleShowRequesterDetails = useCallback(async (person) => {
    const id = person?.requester_id ?? person?.id;
    if (id == null) return;
    setSelectedRequester({
      id,
      username: person.requester_username || person.username,
      avatar_url: person.requester_avatar_url || person.avatar_url,
    });
    setShowRequesterDetailsModal(true);
    try {
      const token = getToken();
      const res = await fetch(`${serverUrl}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSelectedRequester(await res.json());
    } catch (e) {
      // Leave the shell: a name and a picture is better than an error, and the message button
      // works from the id alone.
      console.warn('[Requests] could not load that profile:', e?.message);
    }
  }, [serverUrl]);

  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const handleNewSpot = useCallback((newSpotFromServer) => {
    const formattedSpot = {
      id: newSpotFromServer.id,
      user_id: newSpotFromServer.user_id,
      username: newSpotFromServer.username,
      lat: parseFloat(newSpotFromServer.latitude),
      lng: parseFloat(newSpotFromServer.longitude),
      status: newSpotFromServer.status,
      time_to_leave: newSpotFromServer.time_to_leave,
      price: parseFloat(newSpotFromServer.price),
      comments: newSpotFromServer.comments || '',
      isExactLocation: newSpotFromServer.isExactLocation !== undefined ? newSpotFromServer.isExactLocation : (newSpotFromServer.user_id == currentUserIdRef.current || newSpotFromServer.is_auto_detected),
      is_free: newSpotFromServer.cost_type === 'free',
      declared_at: newSpotFromServer.declared_at,
      cost_type: newSpotFromServer.cost_type,
    };
    setFilteredParkingSpots(prevSpots => [...prevSpots, formattedSpot]);
  }, []);

  const handleSpotDeleted = useCallback((data) => {
    const spotIdToDelete = data?.spotId;
    if (spotIdToDelete) {
      const spotIdInt = parseInt(spotIdToDelete, 10);
      setFilteredParkingSpots(prevSpots => prevSpots.filter(spot => spot.id !== spotIdInt));
      emitter.emit('spotDeleted', data);
      const { ownerId, requesterIds } = data;
      const participants = [ownerId, ...(requesterIds || [])];
      if (requesterIds && requesterIds.includes(currentUserIdRef.current)) {
        setPendingRequests(prevRequests => prevRequests.filter(id => id !== spotIdInt));
        addNotification(`Spot #${spotIdToDelete} is no longer available.`, 'red');
      }
      setAllChatMessages(prevAllMessages => {
        const newAllChatMessages = { ...prevAllMessages };
        participants.forEach(userId => {
          delete newAllChatMessages[userId];
        });
        return newAllChatMessages;
      });
      setUnreadMessages(prevUnread => {
        const newUnreadMessages = { ...prevUnread };
        participants.forEach(userId => {
          delete newUnreadMessages[userId];
        });
        return newUnreadMessages;
      });
      if (chatRecipient && participants.includes(chatRecipient.id)) {
        setIsMessagesDrawerOpen(false);
        setChatRecipient(null);
      }
    }
  }, [chatRecipient, addNotification]);

  useEffect(() => {
    const fetchMe = async () => {
      const token = getToken();
      if (!token) return;

      if (isTokenExpired(token)) {
        logout();
        return;
      }

      try {
        const response = await fetch('/api/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const userData = await response.json();
          setCurrentUserId(userData.id);
          setCurrentUsername(userData.username);
          setCurrentUserCarType(userData.car_type);
          setProfileUserData(userData);
          console.log(`Web App: Logged in as ${userData.username} (Internal ID: ${userData.id})`);

          // SYNC PENDING CAR DETAILS (for Google Sign-up)
          const pendingDetails = localStorage.getItem('pendingCarDetails');
          if (pendingDetails && (!userData.car_type || !userData.plate_number)) {
            const { plateNumber, carColor, carType } = JSON.parse(pendingDetails);
            console.log('Web App: Syncing pending car details after Google login...');
            
            const syncResponse = await fetch(`/api/users/${userData.id}/car-details`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ plate_number: plateNumber, car_color: carColor, car_type: carType }),
            });

            if (syncResponse.ok) {
              const syncData = await syncResponse.json();
              localStorage.setItem('token', syncData.token); // Store the updated token
              localStorage.removeItem('pendingCarDetails'); // Clear the pending details
              
              // Update local state with the newly synced details
              setCurrentUserCarType(carType);
              setProfileUserData(prev => ({ 
                ...prev, 
                plate_number: plateNumber, 
                car_color: carColor, 
                car_type: carType 
              }));
              console.log('Web App: Car details synced successfully!');
            }
          } else if (pendingDetails) {
            // Already have car details, just clear the pending ones
            localStorage.removeItem('pendingCarDetails');
          }
        } else {
          console.error("Failed to fetch user profile, logging out...");
          logout();
        }
      } catch (error) {
        console.error("Error fetching me profile:", error);
      }
    };

    fetchMe();
  }, []);



  const handleRateRequester = useCallback((requester) => {
    setUserToRate(requester);
    setShowRatingModal(true);
  }, []);

  const fetchProfileData = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const token = getToken();
      const response = await fetch(`/api/users/${currentUserId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      const data = await response.json();
      setProfileUserData({
        ...data,
        total_arrival_time: parseFloat(data.total_arrival_time),
        completed_transactions_count: parseInt(data.completed_transactions_count, 10),
      });
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setProfileUserData(null);
    }
  }, [currentUserId]);

  const fetchPendingRequests = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const token = getToken();
      const response = await fetch(`${serverUrl}/api/user/pending-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPendingRequests(data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  }, [currentUserId]);

  const fetchSpotRequests = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const token = getToken();
      const response = await fetch(`${serverUrl}/api/user/spot-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('App.js: fetchSpotRequests - data received:', JSON.stringify(data, null, 2)); // DEBUG LOG
      setSpotRequests(data);
      console.log('App.js: spotRequests state after update:', JSON.stringify(data, null, 2)); // DEBUG LOG
    } catch (error) {
      console.error('Error fetching spot requests:', error);
    }
  }, [currentUserId]);

  const playSound = useCallback(() => {
    if (audioContextRef.current && audioBufferRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBufferRef.current;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    }
  }, []);

  const playSoundRemoveRequest = useCallback(() => {
    if (audioContextRef.current && removeRequestAudioBufferRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      const source = audioContextRef.current.createBufferSource();
      source.buffer = removeRequestAudioBufferRef.current;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    }
  }, []);

  const playSoundAcceptedRequest = useCallback(() => {
    if (audioContextRef.current && acceptedRequestAudioBufferRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      const source = audioContextRef.current.createBufferSource();
      source.buffer = acceptedRequestAudioBufferRef.current;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    }
  }, []);

  const playSoundArrived = useCallback(() => {
    if (audioContextRef.current && arrivedAudioBufferRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      const source = audioContextRef.current.createBufferSource();
      source.buffer = arrivedAudioBufferRef.current;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    }
  }, []);

  useEffect(() => {
    if (currentUserId && currentUsername) {
      register(currentUserId, currentUsername);
    }

    const handleConnect = () => {
      console.log('Web App: Connected to Socket.IO server!');
      if (currentUserId && currentUsername) {
        console.log(`Web App: Re-registering user ${currentUsername} (ID: ${currentUserId})`);
        register(currentUserId, currentUsername);
      }
    };

    socket.on('connect', handleConnect);

    return () => {
      socket.off('connect', handleConnect);
    };
  }, [currentUserId, currentUsername]);

  useEffect(() => {
    socket.on('newParkingSpot', handleNewSpot);
    socket.on('spotDeleted', handleSpotDeleted);
    
    const handleSpotUpdated = (updatedSpotFromServer) => {
      setFilteredParkingSpots(prevSpots =>
        prevSpots.map(spot =>
          spot.id === updatedSpotFromServer.id
            ? {
                ...spot,
                lat: parseFloat(updatedSpotFromServer.latitude),
                lng: parseFloat(updatedSpotFromServer.longitude),
                time_to_leave: updatedSpotFromServer.time_to_leave,
                price: parseFloat(updatedSpotFromServer.price),
                comments: updatedSpotFromServer.comments || '',
                cost_type: updatedSpotFromServer.cost_type,
                declared_at: updatedSpotFromServer.declared_at,
                declared_car_type: updatedSpotFromServer.declared_car_type,
                status: updatedSpotFromServer.status,
                is_free: updatedSpotFromServer.cost_type === 'free',
                isExactLocation: updatedSpotFromServer.isExactLocation !== undefined ? updatedSpotFromServer.isExactLocation : (updatedSpotFromServer.user_id == currentUserId || updatedSpotFromServer.is_auto_detected),
              }
            : spot
        )
      );
    };
    socket.on('spotUpdated', handleSpotUpdated);

    const handleSpotStatusUpdated = (updatedSpotFromServer) => {
      setFilteredParkingSpots(prevSpots =>
        prevSpots.map(spot =>
          spot.id === updatedSpotFromServer.id
            ? {
                ...spot,
                status: updatedSpotFromServer.status,
              }
            : spot
        )
      );
    };
    socket.on('spotStatusUpdated', handleSpotStatusUpdated);

    const handleSpotRequest = (data) => {
      console.log('App.js: Received spotRequest socket event:', JSON.stringify(data, null, 2)); // DEBUG LOG
      addNotification(data.message, 'blue');
      playSound();
      fetchPendingRequests();
      fetchSpotRequests(); // Call the new function
      // Find the spot in filteredParkingSpots and set it to spotToOpenDrawer
      setSpotToOpenDrawer(prev => {
         const requestedSpot = filteredParkingSpots.find(spot => spot.id === data.spotId);
         return requestedSpot || prev;
      });
      console.log('App.js: spotRequests state after spotRequest event (should be updated by fetchSpotRequests):', JSON.stringify(spotRequests, null, 2)); // DEBUG LOG
    };
    socket.on('spotRequest', handleSpotRequest);

    const handleRequestAcceptedOrDeclined = (data) => {
      console.log('App.js: Received requestAcceptedOrDeclined socket event:', JSON.stringify(data, null, 2)); // DEBUG LOG
      fetchSpotRequests();
    };
    socket.on('requestAcceptedOrDeclined', handleRequestAcceptedOrDeclined);

    const handleRequestResponse = (data) => {
      if (data.message.includes('ACCEPTED')) {
        setAcceptedRequestOwnerUsername(data.ownerUsername);
        setShowAcceptedRequestModal(true);
        playSoundAcceptedRequest();
      }
      addNotification(data.message, 'default');
      if (data.spot) {
        const formattedAcceptedSpot = {
          ...data.spot,
          lat: parseFloat(data.spot.latitude),
          lng: parseFloat(data.spot.longitude),
          isExactLocation: true,
        };
        setAcceptedSpot(formattedAcceptedSpot);
        setFilteredParkingSpots(prevSpots => {
          const index = prevSpots.findIndex(s => s.id === data.spot.id);
          if (index !== -1) {
            const newSpots = [...prevSpots];
            newSpots[index] = formattedAcceptedSpot;
            return newSpots;
          }
          return prevSpots;
        });
      }
      if (data.message.includes('DECLINED') || data.message.includes('CANCELLED')) {
        setPendingRequests(prevRequests => prevRequests.filter(id => id !== data.spotId));
      } else if (data.requestId) {
        if (data.message.includes('reactivated') || data.message.includes('ACCEPTED')) {
          setPendingRequests(prevRequests => [...prevRequests, data.spotId || data.spot.id]);
        }
      }
    };
    socket.on('requestResponse', handleRequestResponse);

    const handleRequesterArrived = (data) => {
      const message = `User ${data.requesterUsername} has arrived at spot ${data.spotId}. Please confirm to complete the transaction.`;
      addNotification(message, 'default');
      playSoundArrived();
      setArrivalConfirmationData(data);
      setArrivalConfirmationModalOpen(true);
    };
    socket.on('requesterArrived', handleRequesterArrived);

    const handleRequestCancelled = (data) => {
      // 2026-10-29: an accepted request can be withdrawn now too ("I'm not coming").
      const message = data.wasAccepted && data.message
        ? data.message
        : `User ${data.requesterUsername} has cancelled their request for your spot ${data.spotId}.`;
      addNotification(message, 'purple');
      playSoundRemoveRequest();
      setSpotRequests(prevRequests => prevRequests.filter(req => req.id !== data.requestId));
    };
    socket.on('requestCancelled', handleRequestCancelled);

    // 2026-10-29: owner side — how far away the accepted requester is (distance and ETA only; see
    // server/utils/handoverRules.js), merged into the request it belongs to.
    const handleRequesterProgress = (p) => {
      if (p?.requestId == null) return;
      setSpotRequests((prev) => prev.map((r) => (
        r.id === p.requestId ? { ...r, progress: { distanceM: p.distanceM, etaMin: p.etaMin, at: p.at || Date.now() } } : r
      )));
    };
    socket.on('requesterProgress', handleRequesterProgress);

    // Their wait deadline has passed. Nothing is released by itself any more: the owner is asked, in
    // the Requests panel, whether to keep waiting or release the spot. The requester is told the
    // owner is still holding it.
    const handleRequestLate = (data) => {
      if (data?.role === 'owner') {
        setSpotRequests((prev) => prev.map((r) => (r.id === data.requestId ? { ...r, isLate: true } : r)));
        setIsRequestsPanelOpen(true);
        playSoundArrived();
      }
      if (data?.message) addNotification(data.message, data?.role === 'owner' ? 'red' : 'default');
    };
    socket.on('requestLate', handleRequestLate);

    // An accepted request closed without a handover (released by the owner, or at the backstop). Sent
    // since 2026-09-20 and never listened for here.
    const handleRequestAbandoned = (data) => {
      if (data?.role === 'requester') {
        setAcceptedSpot((prev) => (prev && String(prev.id) === String(data.spotId) ? null : prev));
      }
      if (data?.message) addNotification(data.message, 'default');
      fetchSpotRequests();
    };
    socket.on('requestAbandoned', handleRequestAbandoned);

    // 2026-11-01: the owner ended the spot while we were on our way.
    const handleHandoverCancelled = (data) => {
      setAcceptedSpot((prev) => (prev && String(prev.id) === String(data?.spotId) ? null : prev));
      addNotification(data?.message || 'The owner cancelled, so the spot is no longer held for you.', 'red');
      playSoundRemoveRequest();
    };
    socket.on('handoverCancelled', handleHandoverCancelled);

    const handleTransactionComplete = (data) => {
      fetchProfileData();
      if (data.ownerId && data.ownerUsername) {
        handleRateRequester({ requester_id: data.ownerId, requester_username: data.ownerUsername });
      }
    };
    socket.on('transactionComplete', handleTransactionComplete);

    const handleArrivalRejected = (data) => {
      console.log('Web Client: Arrival rejected received:', data);
      // 2026-09-16: was only this log line, quoting the raw spot id — easy to miss entirely, and the
      // Arrived button silently becoming clickable again was the only other sign. Now a dialog and a
      // sound, the same weight as "Request Accepted", since the requester is standing at the kerb
      // and has to act on it.
      const owner = data.ownerUsername || 'The owner';
      addNotification(`${owner} couldn't see you at the spot yet.`, 'default');
      setArrivalRejectedBy(owner);
      playSoundRemoveRequest();
      // This will trigger a re-render and can be passed down if needed
      setArrivalRejectedSpotId(data.spotId);
      // Reset it after a short delay so the drawer can react
      setTimeout(() => setArrivalRejectedSpotId(null), 1000);
    };
    socket.on('arrivalRejected', handleArrivalRejected);

    const handlePrivateMessage = (message) => {
      const fromId = message.from;
      const messageWithTimestamp = { ...message, timestamp: message.created_at || new Date().toISOString() };
      setAllChatMessages(prev => ({ ...prev, [fromId]: [...(prev[fromId] || []), messageWithTimestamp] }));
      // Was `!isChatOpen` — the retired drawer's flag. The panel is the chat now, and a message
      // only counts as read if its conversation is the one actually on screen.
      if (!isMessagesDrawerOpen || (chatRecipient && chatRecipient.id !== fromId)) {
        setUnreadMessages(prev => ({ ...prev, [fromId]: (prev[fromId] || 0) + 1 }));
      }
    };
    socket.on('privateMessage', handlePrivateMessage);

    return () => {
      socket.off('newParkingSpot', handleNewSpot);
      socket.off('spotDeleted', handleSpotDeleted);
      socket.off('spotUpdated', handleSpotUpdated);
      socket.off('spotStatusUpdated', handleSpotStatusUpdated);
      socket.off('spotRequest', handleSpotRequest);
      socket.off('requestAcceptedOrDeclined', handleRequestAcceptedOrDeclined);
      socket.off('requestResponse', handleRequestResponse);
      socket.off('requesterArrived', handleRequesterArrived);
      socket.off('requestCancelled', handleRequestCancelled);
      socket.off('requesterProgress', handleRequesterProgress);
      socket.off('requestLate', handleRequestLate);
      socket.off('requestAbandoned', handleRequestAbandoned);
      socket.off('handoverCancelled', handleHandoverCancelled);
      socket.off('transactionComplete', handleTransactionComplete);
      socket.off('arrivalRejected', handleArrivalRejected);
      socket.off('privateMessage', handlePrivateMessage);
    };
  }, [currentUserId, handleNewSpot, handleSpotDeleted, addNotification, playSound, fetchPendingRequests, fetchSpotRequests, playSoundAcceptedRequest, playSoundArrived, playSoundRemoveRequest, fetchProfileData, handleRateRequester, isMessagesDrawerOpen, chatRecipient, filteredParkingSpots, spotRequests]);


  // The log for whoever is signed in: read once their id is known, merged with anything added before
  // it was (a message can arrive in the first moments after load), and written back on every change —
  // but only once it has been read, or an empty log at load would overwrite three days of entries.
  useEffect(() => {
    setActivityLoadedFor(null);
    if (!currentUserId) return;
    let stored = [];
    let seen = null;
    try {
      stored = JSON.parse(localStorage.getItem(activityKey(currentUserId)) || '[]');
      seen = Number(localStorage.getItem(activitySeenKey(currentUserId)));
      sessionStorage.removeItem('notificationLog'); // the old home of the log
    } catch (_) { /* unreadable: start empty */ }
    setNotificationLog((live) => mergeActivity(stored, live));
    // First visit: what is already here counts as seen, so the dot means "new since you looked".
    const seenAt = Number.isFinite(seen) && seen > 0 ? seen : Date.now();
    setActivitySeenAt(seenAt);
    try { localStorage.setItem(activitySeenKey(currentUserId), String(seenAt)); } catch (_) { /* best effort */ }
    setActivityLoadedFor(currentUserId);
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId || activityLoadedFor !== currentUserId) return;
    try {
      localStorage.setItem(activityKey(currentUserId), JSON.stringify(pruneActivity(notificationLog)));
    } catch (_) { /* storage full or blocked: the log still works for this visit */ }
  }, [notificationLog, activityLoadedFor, currentUserId]);

  const markActivitySeen = useCallback(() => {
    const now = Date.now();
    setActivitySeenAt(now);
    try { if (currentUserId) localStorage.setItem(activitySeenKey(currentUserId), String(now)); } catch (_) { /* best effort */ }
  }, [currentUserId]);
  const hasUnreadActivity = activitySeenAt != null && notificationLog.some((n) => n.rawTime > activitySeenAt);

  useEffect(() => {
    const initAudio = async () => {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const response = await fetch(newRequestSound);
        const arrayBuffer = await response.arrayBuffer();
        audioBufferRef.current = await audioContextRef.current.decodeAudioData(arrayBuffer);
        const removeRequestResponse = await fetch(removeRequestSound);
        const removeRequestArrayBuffer = await removeRequestResponse.arrayBuffer();
        removeRequestAudioBufferRef.current = await audioContextRef.current.decodeAudioData(removeRequestArrayBuffer);
        const acceptedRequestResponse = await fetch(acceptedRequestSound);
        const acceptedRequestArrayBuffer = await acceptedRequestResponse.arrayBuffer();
        acceptedRequestAudioBufferRef.current = await audioContextRef.current.decodeAudioData(acceptedRequestArrayBuffer);
        const arrivedResponse = await fetch(arrivedSound);
        const arrivedArrayBuffer = await arrivedResponse.arrayBuffer();
        arrivedAudioBufferRef.current = await audioContextRef.current.decodeAudioData(arrivedArrayBuffer);
      } catch (error) {
        console.error("Error initializing audio:", error);
      }
    };
    initAudio();
  }, []);

  // Browsers start an AudioContext suspended and only let it resume from a real user gesture, so
  // every sound in this app depends on this unlock. The play functions' own resume() calls cannot do
  // it: they run from socket handlers, not gestures, and the browser silently ignores them.
  //
  // 2026-09-15: the owner's new-request beep stopped after a deploy. This listener had two gaps that
  // a freshly loaded page walks straight into:
  //
  //   - It listened for 'click' on document.body in the BUBBLING phase. Anything that stops
  //     propagation — the map, drawers, most modal libraries — keeps the click from ever reaching
  //     body, so on a map-centric page the gesture a user is most likely to make never unlocked it.
  //   - It removed itself after the first click whether or not audio actually resumed, so one
  //     click that failed to unlock spent the only attempt and left the page silent for the rest of
  //     the session, with no error anywhere.
  //
  // Now: capture phase on window, so no child can swallow the gesture; the full set of events
  // browsers accept as user activation, since Safari and Chrome disagree on which count; and it stays
  // attached until the context reports 'running'.
  useEffect(() => {
    const events = ['pointerdown', 'keydown', 'touchend', 'click'];
    const detach = () => events.forEach((e) => window.removeEventListener(e, unlockAudio, true));
    function unlockAudio() {
      const ctx = audioContextRef.current;
      if (!ctx) return; // initAudio hasn't created it yet — keep listening
      if (ctx.state === 'running') { detach(); return; }
      if (ctx.state === 'suspended') {
        ctx.resume()
          .then(() => { if (ctx.state === 'running') detach(); })
          .catch(() => {}); // a refused resume leaves the listeners in place for the next gesture
      }
    }
    events.forEach((e) => window.addEventListener(e, unlockAudio, true));
    return detach;
  }, []);

  const [expiredNotifiedSpots, setExpiredNotifiedSpots] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      filteredParkingSpots.forEach(spot => {
        if (spot.time_to_leave && !expiredNotifiedSpots.includes(spot.id)) {
          const declaredAt = new Date(spot.declared_at);
          const now = new Date();
          const minutesSinceDeclared = (now - declaredAt) / 60000;
          if (minutesSinceDeclared > spot.time_to_leave) {
            addNotification(`Spot ${spot.id} expired`, 'red');
            setExpiredNotifiedSpots(prev => [...prev, spot.id]);
            setExpiredSpotIds(prev => [...prev, spot.id]); // Add spot ID to expiredSpotIds
          }
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [filteredParkingSpots, expiredNotifiedSpots, addNotification, expiredSpotIds]); // Add expiredSpotIds to dependencies



  useEffect(() => {
    if (currentUserId) {
      fetchProfileData();
    }
  }, [currentUserId, fetchProfileData]);

  const handleCarDetailsUpdated = useCallback(() => {
    const token = getToken();
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setCurrentUserId(decodedToken.userId);
        setCurrentUsername(decodedToken.username);
        setCurrentUserCarType(decodedToken.carType);
        fetchProfileData();
      } catch (error) {
        console.error("Error decoding token after car details update:", error);
        logout();
      }
    }
  }, [fetchProfileData]);

  const handleRequestStatusChange = useCallback((spotId, status) => {
    return new Promise(resolve => {
      setPendingRequests(prevRequests => {
        if (status === 'requested') {
          resolve(spotId);
          return [...prevRequests, spotId];
        } else if (status === 'cancelled') {
          resolve(spotId);
          return prevRequests.filter(id => id !== spotId);
        }
        resolve(null);
        return prevRequests;
      });
    });
  }, []);

  const fetchParkingSpots = useCallback(async (fitsOnly, userCarType, radiusKm, around) => {
    let url = '/api/parkingspots';
    const params = new URLSearchParams();
    // Only when the owner has asked for it. Sending userCarType is what turns the server's
    // car-size filter on, so an always-on toggle here was an always-on filter there.
    if (fitsOnly && userCarType) {
      params.append('userCarType', userCarType);
    }
    // 2026-10-17: the radius had never been sent. selectedRadius was state nobody read — set in
    // App, handed to the settings UI, rendered in a dropdown, and that was the whole of its life.
    // So "Show spots around radius of: 5km" has been decorative since it was added, and the client
    // was downloading every spot the server would give it.
    //
    // The server has supported this since 2026-08-26 (PostGIS, ST_DWithin) and already exempts
    // your own spot and anything you have asked for, whatever the distance — so turning it on
    // cannot hide the things you most need to see. Omitted while the browser has not resolved a
    // position yet, which the server reads as no radius filtering at all.
    if (around && radiusKm) {
      params.append('lat', around[0]);
      params.append('lon', around[1]);
      params.append('radiusKm', radiusKm);
    }
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    try {
      const token = getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers: headers, cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const formattedSpots = data.map(spot => ({
        ...spot,
        lat: parseFloat(spot.latitude),
        lng: parseFloat(spot.longitude),
        price: parseFloat(spot.price),
        comments: spot.comments || '',
        // Use server-provided isExactLocation, but fallback to client-side check if missing
        isExactLocation: spot.isExactLocation !== undefined ? spot.isExactLocation : (spot.user_id == currentUserId || spot.is_auto_detected),
      }));
      setFilteredParkingSpots(formattedSpots);
    } catch (error) {
      console.error('Error fetching parking spots:', error);
    }
  }, [currentUserId]);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = getToken();
      if (token && isTokenExpired(token)) {
        logout();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchParkingSpots(fitsMyCarOnly, currentUserCarType, selectedRadius, userLocation);
  }, [fetchParkingSpots, fitsMyCarOnly, currentUserCarType, selectedRadius, userLocation]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const handleConfirmArrival = () => {
    if (arrivalConfirmationData) {
      socket.emit('confirm-transaction', {
        spotId: arrivalConfirmationData.spotId,
        requesterId: arrivalConfirmationData.requesterId,
      });
      setArrivalConfirmationModalOpen(false);
      addNotification('Arrival confirmed!', 'green');
      handleRateRequester({ requester_id: arrivalConfirmationData.requesterId, requester_username: arrivalConfirmationData.requesterUsername });
      setArrivalConfirmationData(null);
    }
  };

  const handleCloseArrivalModal = () => {
    setArrivalConfirmationModalOpen(false);
    setArrivalConfirmationData(null);
  };

  const handleNotIdentified = () => {
    if (arrivalConfirmationData) {
      console.log(`Owner did not identify requester for spot ${arrivalConfirmationData.spotId}`);
      socket.emit('reject-arrival', {
        spotId: arrivalConfirmationData.spotId,
        requesterId: arrivalConfirmationData.requesterId,
      });
      addNotification(`You have indicated that the requester was not identified.`, 'default');
    }
    setArrivalConfirmationModalOpen(false);
    setArrivalConfirmationData(null);
  };

  const handleRate = async (rating) => {
    if (!userToRate) return;
    try {
      const token = getToken();
      const response = await fetch(`${serverUrl}/api/users/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        // request_id names WHICH handover is being rated. The server treats it as optional and
        // resolves the newest unrated one when it is missing, which is right for the post-handover
        // prompt (it knows only the person) and wrong for a history row, where the reader has
        // pointed at a particular one. See POST /api/users/rate.
        body: JSON.stringify({
          rated_user_id: userToRate.requester_id,
          rating,
          ...(userToRate.request_id != null ? { request_id: userToRate.request_id } : {}),
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to submit rating');
      }
      addNotification('Rating submitted successfully!', 'green');
      userToRate.onRated?.();
      setShowRatingModal(false);
      setUserToRate(null);
    } catch (error) {
      console.error('Error submitting rating:', error);
      addNotification('Failed to submit rating', 'red');
    }
  };

  // Declining a request from the Requests panel. The same socket event SideDrawer emits, so this
  // adds a second place to press it, not a second way for it to work.
  //
  // 2026-10-17: there was a handleAcceptFromPanel beside this one and it is gone with the button.
  // Accepting is a promise a stranger collects by driving to you and needs you at the space to hand
  // it over; declining releases an obligation and can honestly be done from anywhere.
  const handleDeclineFromPanel = useCallback((r) => {
    socket.emit('declineRequest', {
      requestId: r.id,
      requesterId: r.requester_id,
      spotId: r.spotId,
      ownerUsername: currentUsername,
      ownerId: currentUserId,
    });
    addNotification(`Request from ${r.requester_username} rejected!`, 'red');
    emitter.emit('request-rejected-by-owner', r.id);
  }, [currentUsername, currentUserId, addNotification]);

  // Withdrawing one of your own. Map.js has had this since requests existed; this is the same call
  // against the same endpoint, reachable without having to find the spot on the map first.
  const handleCancelFromPanel = useCallback(async (spotId) => {
    try {
      const token = getToken();
      const response = await fetch(`${serverUrl}/api/cancel-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ spotId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        addNotification(`Failed to cancel request: ${err.message || response.status}`, 'red');
        return;
      }
      addNotification('Request cancelled.', 'default');
      // 2026-10-29: an accepted one can be withdrawn too — the acceptance goes with it.
      setAcceptedSpot((prev) => (prev && String(prev.id) === String(spotId) ? null : prev));
      setPendingRequests((prev) => prev.filter((id) => id !== spotId));
      setFilteredParkingSpots((prev) =>
        prev.map((s) => (s.id === spotId ? { ...s, my_request_status: null } : s))
      );
      emitter.emit('spot-request-updated', spotId);
    } catch (error) {
      console.error('Error cancelling request:', error);
      addNotification('An error occurred while cancelling the request.', 'red');
    }
  }, [serverUrl, addNotification]);

  // 2026-10-29: "Release spot" — stop waiting for a requester whose deadline has passed. The server
  // refuses it before then, and answers with the outcome.
  const handleReleaseLate = useCallback((requestId) => {
    socket.emit('releaseLateRequest', { requestId }, (result) => {
      if (result?.ok) fetchSpotRequests();
      else if (result?.reason !== 'not-late') addNotification('Could not release the spot. Try again.', 'red');
    });
  }, [addNotification, fetchSpotRequests]);

  const clearUnreadMessages = (userId) => {
    setUnreadMessages((prev) => {
      const newUnread = { ...prev };
      delete newUnread[userId];
      return newUnread;
    });
  };

  // The ✉ on a profile card. 2026-10-18: this used to open ChatSideDrawer — a SECOND chat
  // surface, in the old purple language, on the opposite side of the screen from the Messages
  // panel, showing the same messages. Both could be open at once, and were: reported with a
  // screenshot of one conversation rendered twice.
  //
  // One surface now. The panel takes the person and opens straight onto them, so this only has to
  // say who and open it.
  const handleOpenChat = useCallback((recipient) => {
    if (!recipient?.id) return;
    setChatRecipient(recipient);
    setIsMessagesDrawerOpen(true);
    setShowProfileModal(false);
    setIsRequestsPanelOpen(false);
  }, []);

  const handleDeleteSpot = useCallback(async (spotId) => {
    try {
      const token = getToken();
      const response = await fetch(`${serverUrl}/api/parkingspots/${spotId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      addNotification(`Spot #${spotId} deleted successfully.`, 'green');
      setFilteredParkingSpots(prevSpots => prevSpots.filter(spot => spot.id !== spotId));
    } catch (error) {
      console.error('Error deleting spot:', error);
      addNotification(`Failed to delete spot: ${error.message}`, 'red');
    }
  }, [addNotification]);

  // 2026-10-15: this used to fall back to [51.505, -0.09] — which is London, and specifically the
  // coordinate from Leaflet's own documentation example. Field-reported: signing in showed a map of
  // London, and only a couple of refreshes later the real location.
  //
  // Two things made that stick. getCurrentPosition was one-shot with no options, so a single
  // transient failure (common on desktop, where there is no GPS and the browser is guessing from
  // Wi-Fi) was final for the whole session. And the map is mounted only once userLocation is
  // non-null — so the fallback did not merely fill a gap, it DECIDED the map's centre, and
  // react-leaflet's MapContainer ignores `center` after mount, so nothing could correct it.
  //
  // Now: options so it resolves rather than hangs, a retry before giving up, and — most of all —
  // no invented location. If we cannot find the user we keep saying so, because a map confidently
  // centred on the wrong city is worse than a map that admits it is still looking.
  useEffect(() => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser.');
      setLocationError('This browser cannot report your location.');
      return;
    }
    let cancelled = false;
    const opts = { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 };
    const ok = (pos) => {
      if (cancelled) return;
      setLocationError(null);
      setUserLocation([pos.coords.latitude, pos.coords.longitude]);
    };
    const giveUp = (err) => {
      if (cancelled) return;
      console.error('Error getting location: ', err);
      setLocationError(err?.code === 1
        ? 'Udal needs your location to show the map. Allow it in your browser settings and reload.'
        : 'Could not work out where you are. Check your connection and reload.');
    };
    // One retry, with high accuracy on the second attempt: a desktop browser's first answer is
    // often a timeout while it consults the network, and the second usually succeeds.
    navigator.geolocation.getCurrentPosition(ok, () => {
      if (cancelled) return;
      navigator.geolocation.getCurrentPosition(ok, giveUp, { ...opts, enableHighAccuracy: true, timeout: 15000 });
    }, opts);
    return () => { cancelled = true; };
  }, []);

  // Which of the three side panels is showing, for the header button that opens it. Derived rather
  // than a fourth piece of state: three booleans and a name for them would be four ways to say the
  // same thing, and three of them could disagree.
  const openPanel = showProfileModal ? 'profile'
    : isMessagesDrawerOpen ? 'messages'
    : isRequestsPanelOpen ? 'requests'
    : null;

  const handleLogoClick = () => {
    setIsLogoAnimating(true);
    setTimeout(() => {
      setIsLogoAnimating(false);
    }, 1000);
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-brand-row">
          <div className="logo-title-container">
            <img
              src={logo}
              className={`logo-img ${isLogoAnimating ? 'logo-animate' : ''}`}
              alt="Udal Logo"
              onClick={handleLogoClick}
            />
            {/* 2026-10-14: the tagline that stood here ("the app you need to park in the city!")
                was marketing copy inside application chrome — it belongs on the landing page, which
                now says it properly, and every pixel it took came out of the map. */}
            <div className="logo-container">
              <h1 className="logo">Udal</h1>
            </div>
          </div>
        </div>
      </header>
      
      <div className="main-content">
        <Filter 
          currentUsername={currentUsername} 
          currentUserAvatarUrl={profileUserData?.avatar_url}
          // One rail, one panel. All three now occupy the same strip down the right edge, so two
          // open at once would stack exactly on top of each other — the one underneath invisible,
          // still "open", and its button still lit. Each of these opens its own and closes the
          // others, which is also what a reader expects of a single side panel.
          //
          // fetchProfileData only on the way IN: refetching as it closes is a request for a panel
          // nobody is looking at.
          openPanel={openPanel}
          onAvatarClick={() => {
            const next = !showProfileModal;
            if (next) fetchProfileData();
            setShowProfileModal(next);
            setIsMessagesDrawerOpen(false);
            setIsRequestsPanelOpen(false);
          }}
          onMessagesClick={() => {
            setIsMessagesDrawerOpen((v) => !v);
            setShowProfileModal(false);
            setIsRequestsPanelOpen(false);
          }}
          onRequestsClick={() => {
            setIsRequestsPanelOpen((v) => !v);
            setShowProfileModal(false);
            setIsMessagesDrawerOpen(false);
          }}
          requestCount={spotRequests.length + pendingRequests.length}
          unreadMessages={unreadMessages}
        />
        <div className="map-container">
          {userLocation ? (
            <Map
              parkingSpots={filteredParkingSpots}
              userLocation={userLocation}
              currentUserId={currentUserId}
              acceptedSpot={acceptedSpot}
              requesterEta={requesterEta}
              onSpotDeleted={handleDeleteSpot}
              addNotification={addNotification}
              onRequestStatusChange={handleRequestStatusChange}
              currentUsername={currentUsername}
              pendingRequests={pendingRequests}
              spotRequests={spotRequests}
              onOpenChat={handleOpenChat}
              unreadMessages={unreadMessages}
              onRateRequester={handleRateRequester}
              onOpenRequesterDetails={handleShowRequesterDetails}
              isMessagesDrawerOpen={isMessagesDrawerOpen}
              setIsMessagesDrawerOpen={setIsMessagesDrawerOpen}
              serverUrl={serverUrl}
              expiredSpotIds={expiredSpotIds} // Pass expiredSpotIds to Map
              spotToOpenDrawer={spotToOpenDrawer} // Pass spotToOpenDrawer to Map
              arrivalRejectedSpotId={arrivalRejectedSpotId} // Pass arrivalRejectedSpotId to Map
            />
          ) : (
            <div className="map-placeholder">
              {locationError || 'Finding where you are\u2026'}
            </div>
          )}
        </div>
        {/* 2026-10-17: "I'm leaving" is gone from the web, deliberately. See the note at the top
            of this file — a declare is a promise a stranger collects by driving, and a browser
            cannot keep it: the spot is public the instant it is made (server/index.js's
            initialStatus) and nothing but its own expiry timer ever removes it. On the phone even
            a hand-made spot is adopted by the engine (SpotContext's adoptIntoEngine), so driving
            off corrects the row. A laptop has no engine, and the pin was an arbitrary map point
            rather than where the device actually was. */}
      </div>
      {/* 2026-10-28: the log that sat here lives in Profile → Activity now, as on the phone. The map
          keeps only this: each entry, briefly, as it happens. */}
      <ActivityToast entries={notificationLog} />
      {/* 2026-10-28: once per browser, on the first signed-in visit — see AppPromoCard. */}
      {currentUserId && showAppPromo && <AppPromoCard onClose={() => setShowAppPromo(false)} />}
      {currentUserId && (
        <RequestsPanel
          open={isRequestsPanelOpen}
          onClose={() => setIsRequestsPanelOpen(false)}
          spotRequests={spotRequests}
          parkingSpots={filteredParkingSpots}
          serverUrl={serverUrl}
          onOpenRequester={(r) => { setIsRequestsPanelOpen(false); handleShowRequesterDetails(r); }}
          onSelectSpot={(spot) => { setIsRequestsPanelOpen(false); setSpotToOpenDrawer(spot); }}
          onDecline={handleDeclineFromPanel}
          onCancelRequest={handleCancelFromPanel}
          onMessage={(r) => handleOpenChat({ id: r.requester_id, username: r.requester_username, avatar_url: r.requester_avatar_url })}
          onRelease={(r) => handleReleaseLate(r.id)}
          onRate={(tx, onRated) => {
            // The modal reads the owner's column names; a history row is the same person under
            // different ones. onRated lets the panel mark the row without re-reading the list.
            setUserToRate({
              requester_id: tx.other_user_id,
              requester_username: tx.other_username,
              request_id: tx.id,
              onRated,
            });
            setShowRatingModal(true);
          }}
        />
      )}
      {currentUserId && (
        <MessagesSideDrawer
          isOpen={isMessagesDrawerOpen}
          openWith={chatRecipient}
          onOpened={() => setChatRecipient(null)}
          onClose={() => setIsMessagesDrawerOpen(false)}
          allChatMessages={allChatMessages}
          unreadMessages={unreadMessages}
          currentUserId={currentUserId}
          clearUnreadMessages={clearUnreadMessages}
          // handleShowRequesterDetails fetches the full person before showing the card — the
          // conversation only knows an id, a name and an avatar.
          onOpenProfile={handleShowRequesterDetails}
        />
      )}
      {/* "© 2026 UDAL LIMITED", the same way round as landing.html's footer — the app and the page that
          advertises it should not write the same line two ways. The year is read from the clock
          for the same reason it is there: a hardcoded one is wrong every January, quietly. */}
      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} UDAL LIMITED</p>
      </footer>
      {arrivalRejectedBy && (
        <ArrivalRejectedModal
          onClose={() => setArrivalRejectedBy(null)}
          ownerUsername={arrivalRejectedBy}
        />
      )}
      {showAcceptedRequestModal && (
        <AcceptedRequestModal 
          onClose={() => setShowAcceptedRequestModal(false)} 
          ownerUsername={acceptedRequestOwnerUsername} 
        />
      )}
      {/* 2026-10-15: the hamburger is gone. It held a settings dialog, an admin page and log out
            — three unrelated things with nowhere else to live, which is what a hamburger usually
            is. All three are about the person using the app, so they are in Profile with the rest
            of what is about them, and the bar has one fewer control nobody could name. */}
      {/* 2026-10-18: was `showProfileModal && <ProfileModal>`, so it was UNMOUNTED when closed and
          vanished instantly — no slide-out, unlike Requests and Messages, which stay mounted and
          animate. Always rendered now, with `open` doing the work, which is what Panel was built
          for ("it stays MOUNTED while closed... a panel that vanishes the instant it is dismissed
          reads as a glitch rather than a movement"). */}
      {(
        <ProfileModal
          open={showProfileModal}
          fitsMyCarOnly={fitsMyCarOnly}
          onFitsMyCarOnlyChange={setFitsMyCarOnly}
          selectedRadius={selectedRadius}
          onRadiusChange={setSelectedRadius}
          onOpenOps={role === 'admin' ? () => navigate('/ops') : undefined}
          onLogout={handleLogout}
          onClose={() => setShowProfileModal(false)}
          userData={profileUserData}
          currentUserId={currentUserId}
          addNotification={addNotification}
          onCarDetailsUpdated={handleCarDetailsUpdated}
          onProfileUpdate={fetchProfileData}
          activity={notificationLog}
          activitySeenAt={activitySeenAt}
          hasUnreadActivity={hasUnreadActivity}
          onActivitySeen={markActivitySeen}
        />
      )}
      <ArrivalConfirmationModal
        isOpen={isArrivalConfirmationModalOpen}
        onClose={handleCloseArrivalModal}
        onConfirm={handleConfirmArrival}
        onNotIdentified={handleNotIdentified}
        isOwner={true}
        requesterUsername={arrivalConfirmationData?.requesterUsername}
        spotId={arrivalConfirmationData?.spotId}
      />
      <>
        {showRatingModal && (
          <RatingModal
            isOpen={showRatingModal}
            onClose={() => setShowRatingModal(false)}
            requester={userToRate}
            onRate={handleRate}
          />
        )}
        {showRequesterDetailsModal && selectedRequester && (
          <RequesterDetailsModal
            isOpen={showRequesterDetailsModal}
            onClose={() => setShowRequesterDetailsModal(false)}
            requester={selectedRequester}
            onOpenChat={handleOpenChat}
          />
        )}
      </>
    </div>
  );
}

function App() {
  useEffect(() => {
    document.body.style.setProperty('--background-image-url', `url(${backgroundImage})`);
  }, []);
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MainAppContent serverUrl={SERVER_URL} />
              </ProtectedRoute>
            }
          />
          {/* 2026-10-17: a route rather than a panel — a map plus a list of up to 200 rows needs
              the room, and the Back button should work. See ParkingHistory.js. */}
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <ParkingHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ops"
            element={
              <AdminRoute>
                <OpsMap serverUrl={SERVER_URL} />
              </AdminRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
